import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { SITE } from "@/lib/siteData";
import { SITE_WIDE_JSONLD } from "@/lib/seoSchemas";

/**
 * Per-page SEO component.
 *
 * Renders title, meta, canonical, OG/Twitter tags and JSON-LD for the route.
 * The Organization + WebSite JSON-LD blocks are auto-injected on EVERY page so
 * search engines always identify us as a Cape Town film & video production
 * company (and never confuse us with the unrelated "Pie in the Sky Bakery").
 *
 * Props:
 *   - title:        FULL <title> (e.g. "Video Production in Cape Town | Pie in the Sky Productions").
 *                   If not provided, a sensible fallback is used.
 *   - description:  meta description (150-160 chars ideal)
 *   - path:         route path WITHOUT trailing host (e.g. "/services/commercials")
 *   - image:        absolute URL for og:image / twitter:image (defaults to brand hero)
 *   - type:         og:type (default "website"; use "article" for blog posts)
 *   - jsonLd:       per-page JSON-LD (array or single object) APPENDED to site-wide schemas
 *   - noindex:      if true, emits <meta name="robots" content="noindex,nofollow">
 */
export const SEO = ({
  title,
  description,
  path = "",
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) => {
  const fullTitle = title || `${SITE.brand} - Cape Town Film & Video Agency`;
  const url = `${SITE.url}${path || ""}`;
  const desc = description || SITE.tagline;
  const ogImage = image || "https://customer-assets.emergentagent.com/job_migration-preserve/artifacts/t19pj3p7_Transparent%20New%20logo%20White%20Text.png";

  // Per-page + site-wide schemas merged. Site-wide first for crawler priority.
  const perPage = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const allSchemas = [...SITE_WIDE_JSONLD, ...perPage];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [path]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={url} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE.brand} />
      <meta property="og:locale" content="en_ZA" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {allSchemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

// ---- Re-export schema helpers from /lib/seoSchemas (back-compat shims) -------
export {
  ORGANIZATION_SCHEMA,
  WEBSITE_SCHEMA,
  SITE_WIDE_JSONLD,
  breadcrumbSchema,
  serviceSchema,
  blogPostingSchema,
  faqSchema,
} from "@/lib/seoSchemas";

// Callable function shims (back-compat for pages that import these as ()-call helpers)
import { ORGANIZATION_SCHEMA as _ORG } from "@/lib/seoSchemas";
export const organizationSchema = () => _ORG;
export const localBusinessSchema = () => _ORG;
